import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { DiscountsTableRow, PaginatedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { discountsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to delete a discount
 */
export const deleteDiscount = async (
	servicesProviderId: string,
	discountId: string,
): Promise<void> => {
	await apiClient.delete(discountsEndpoints.delete(servicesProviderId, discountId));
};

/**
 * Hook for deleting a discount with cache invalidation
 */
export const useDeleteDiscount = () => {
	const queryClient = useQueryClient();
	const servicesProviderId = useCurrentServicesProviderId();

	return useMutation({
		mutationFn: async (discountId: string) => {
			if (!servicesProviderId) {
				throw new Error('Cannot delete discount without services provider ID');
			}
			return deleteDiscount(servicesProviderId, discountId);
		},
		onSuccess: (_data, discountId) => {
			// Remove the deleted discount from all cached search results so the table
			// updates immediately. We intentionally skip invalidateQueries here because
			// the backend search index has eventual consistency — an immediate refetch
			// would return stale data that still includes the deleted item, causing it
			// to briefly reappear. The cache will go stale naturally and refetch
			// consistent data on the next navigation or window refocus.
			queryClient.setQueriesData<PaginatedResponse<DiscountsTableRow>>(
				{ queryKey: queries.discounts.list._def },
				(oldData) => {
					if (!oldData) return oldData;
					return {
						...oldData,
						count: oldData.count - 1,
						data: oldData.data.filter((d) => d.id !== discountId),
					};
				},
			);
		},
	});
};
